import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { toast } from "sonner";
import {
    FileText,
    Image as ImageIcon,
    Download,
    AlertCircle,
    Sparkles,
} from "lucide-react";
import type { AssistantAttachment } from "../types/assistant";
import { formatFileSize } from "../lib/gcsUpload";
import { Throbber } from "./ui/throbber";

const ATTACHMENT_URL_MUTATION = gql`
    mutation AssistantConversationAttachmentUrl($attachmentId: UUID!) {
        assistantConversationAttachmentUrl(attachmentId: $attachmentId) {
            id
            presignedUrl
        }
    }
`;

const SET_USE_IN_ANSWER_MUTATION = gql`
    mutation AssistantConversationAttachmentSetUseInAnswer(
        $attachmentId: UUID!
        $enabled: Boolean!
    ) {
        assistantConversationAttachmentSetUseInAnswer(
            attachmentId: $attachmentId
            enabled: $enabled
        ) {
            id
            useInAnswer
        }
    }
`;

interface AssistantMessageAttachmentsProps {
    /** Files the support agent uploaded on this turn. Empty renders nothing. */
    attachments?: AssistantAttachment[];
    /** Compact mode for the inline-extension surface (Neptune). */
    compact?: boolean;
    /**
     * Show the "Für Antwort verwenden" toggle. Only meaningful where a drafted
     * reply exists to regenerate, so the standalone chat page leaves it off.
     */
    showUseInAnswer?: boolean;
}

/**
 * Chip row for the files attached to a sent chat turn.
 *
 * The chat agent always receives these, so the chip carries no "sent to agent"
 * state. What it does carry is the separate answer-generation opt-in, and an
 * honest account of how the file reached the model: a Word file is text only,
 * and a file whose extraction came back empty is called out so the operator
 * knows the agent could not read it.
 */
export function AssistantMessageAttachments({
    attachments,
    compact = false,
    showUseInAnswer = false,
}: AssistantMessageAttachmentsProps) {
    const client = useApolloClient();
    // Optimistic overlay so the toggle responds immediately; the server value is
    // authoritative and wins again on the next refetch.
    const [pendingToggles, setPendingToggles] = useState<Record<string, boolean>>({});

    // Retire each optimistic entry as soon as the server value agrees with it (or
    // its file disappears). Without this the overlay outlives the mutation it was
    // covering and permanently masks `attachment.useInAnswer` for that id, so a
    // later value arriving from anywhere else — a refetch, the other surface — is
    // ignored and the chip shows a state the backend no longer holds.
    useEffect(() => {
        setPendingToggles((prev) => {
            const settled = Object.entries(prev).filter(([id, value]) => {
                const row = attachments?.find((candidate) => candidate.id === id);
                return row === undefined || row.useInAnswer === value;
            });
            if (settled.length === 0) return prev;
            const next = { ...prev };
            for (const [id] of settled) delete next[id];
            return next;
        });
    }, [attachments]);

    if (!attachments || attachments.length === 0) return null;

    const padding = compact ? "px-2 py-0.5" : "px-2.5 py-1";
    const textSize = compact ? "text-[11px]" : "text-xs";

    const openAttachment = async (attachment: AssistantAttachment) => {
        try {
            const result = await client.mutate<{
                assistantConversationAttachmentUrl: { presignedUrl: string };
            }>({
                mutation: ATTACHMENT_URL_MUTATION,
                variables: { attachmentId: attachment.id },
            });
            const url = result.data?.assistantConversationAttachmentUrl?.presignedUrl;
            if (!url) throw new Error("Keine Download-URL erhalten.");
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (error) {
            toast.error(`"${attachment.name}" konnte nicht geöffnet werden`, {
                description: error instanceof Error ? error.message : String(error),
            });
        }
    };

    const toggleUseInAnswer = async (attachment: AssistantAttachment, next: boolean) => {
        setPendingToggles((prev) => ({ ...prev, [attachment.id]: next }));
        try {
            await client.mutate({
                mutation: SET_USE_IN_ANSWER_MUTATION,
                variables: { attachmentId: attachment.id, enabled: next },
            });
            toast.success(
                next
                    ? `"${attachment.name}" wird für die Antwort verwendet`
                    : `"${attachment.name}" wird nicht mehr für die Antwort verwendet`,
                { description: "Der Antwortentwurf wird neu erstellt." },
            );
        } catch (error) {
            setPendingToggles((prev) => {
                const { [attachment.id]: _dropped, ...rest } = prev;
                return rest;
            });
            toast.error("Einstellung konnte nicht gespeichert werden", {
                description: error instanceof Error ? error.message : String(error),
            });
        }
    };

    return (
        <div className="mt-1.5 flex flex-col items-end gap-1">
            {attachments.map((attachment) => {
                const isImage = attachment.contentType.startsWith("image/");
                const useInAnswer = pendingToggles[attachment.id] ?? attachment.useInAnswer;
                // Only a text-only file depends on extraction: a PDF or image is handed
                // to the model as the file itself, so it stays fully readable even when
                // extraction produced nothing. Flagging those as unreadable told the
                // operator the agent was blind to a document it had in fact just read.
                const unreadable =
                    attachment.textOnly &&
                    attachment.isProcessed &&
                    !attachment.hasExtractedText;
                return (
                    <div
                        key={attachment.id}
                        className={`flex max-w-full items-center gap-1.5 ${padding} overflow-hidden rounded-md border border-border/50 bg-muted/50`}
                    >
                        {!attachment.isProcessed ? (
                            <Throbber
                                className="h-3 w-3 flex-shrink-0 text-muted-foreground"
                                aria-label="Datei wird verarbeitet"
                            />
                        ) : isImage ? (
                            <ImageIcon className="h-3 w-3 flex-shrink-0" />
                        ) : (
                            <FileText className="h-3 w-3 flex-shrink-0" />
                        )}

                        <button
                            type="button"
                            onClick={() => void openAttachment(attachment)}
                            className={`${textSize} flex min-w-0 cursor-pointer items-center gap-1 truncate text-foreground hover:text-primary-accent`}
                            title={`${attachment.name} (${formatFileSize(Number(attachment.size))}) öffnen`}
                        >
                            <span className="truncate">{attachment.name}</span>
                            <Download className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                        </button>

                        {attachment.textOnly && (
                            <span
                                className="flex-shrink-0 text-[10px] text-muted-foreground"
                                title="Word-Dateien erreichen das Modell nur als extrahierter Text, nicht als Originaldatei."
                            >
                                nur Text
                            </span>
                        )}

                        {unreadable && (
                            <span
                                className="flex flex-shrink-0 items-center gap-0.5 text-[10px] text-destructive"
                                title="Der Inhalt dieser Datei konnte nicht gelesen werden. Der Assistent kennt sie nur dem Namen nach."
                            >
                                <AlertCircle className="h-3 w-3" />
                                nicht lesbar
                            </span>
                        )}

                        {showUseInAnswer && (
                            <button
                                type="button"
                                onClick={() => void toggleUseInAnswer(attachment, !useInAnswer)}
                                aria-pressed={useInAnswer}
                                className={`flex flex-shrink-0 cursor-pointer items-center gap-0.5 rounded px-1 text-[10px] transition-colors ${
                                    useInAnswer
                                        ? "text-primary-accent"
                                        : "text-muted-foreground hover:text-primary-accent"
                                }`}
                                title={
                                    useInAnswer
                                        ? "Diese Datei wird für die Antwortgenerierung verwendet. Klicken zum Deaktivieren."
                                        : "Diese Datei für die Antwortgenerierung verwenden."
                                }
                            >
                                <Sparkles className="h-3 w-3" />
                                Für Antwort
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
